'use client'

import {
  createFormHook,
  createFormHookContexts
} from '@tanstack/react-form-nextjs'

import { MapSelector } from '@/components/form/map-selector/map-selector'
import { MultiImagesUpload } from '@/components/form/multi-images-upload'
import { SingleImageUpload } from '@/components/form/single-image-upload'

import { useRevalidateFormOnLocaleChange } from '@/hooks/use-revalidate-form-on-locale-change'

import { Autocomplete } from './autocomplete'
import { AutocompleteGrouped } from './AutocompleteGrouped'
import { Button, ButtonGroup } from './button'
import { ButtonSelector } from './button-selector'
import { Card } from './card'
import { Checkbox } from './checkbox'
import { Combobox } from './combobox'
import { CountryCodeSelectorField } from './country-code-selector'
import { DateInput } from './date-input'
import { DatePicker } from './date-picker'
import { DatePicker as DatePickerField } from './date-picker-field'
import { DateTimePicker } from './date-time-picker'
import { DaysOfWeekSelector } from './days-of-week-selector'
import { DurationPicker } from './duration-picker'
import { FileInput } from './file-input'
import { FileUpload } from './file-upload'
import { ImageComponent } from './image-component'
import { Input } from './input'
import { MultiCombobox } from './multi-combobox'
import { MultiSelect } from './multi-select'
import { OTP } from './otp'
import { Password } from './password'
import { PhoneInputField } from './phone-input'
import { RadioButton } from './radio-button'
import { RichTextField } from './rich-text-field'
import { Select } from './select'
import { SubmitButton } from './submit-button'
import { Switch } from './switch'
import { TextArea } from './text-area'
import { TimePicker } from './time-picker'

// Export FormField components
export { FormField } from './form-field'

export const { fieldContext, formContext, useFieldContext, useFormContext } =
  createFormHookContexts()

const {
  useAppForm: useAppFormBase,
  withFieldGroup,
  withForm
} = createFormHook({
  fieldComponents: {
    Autocomplete,
    AutocompleteGrouped,
    Button,
    ButtonGroup,
    ButtonSelector,
    Card,
    Checkbox,
    Combobox,
    CountryCodeSelector: CountryCodeSelectorField,
    DateInput,
    DatePicker,
    DatePickerField,
    DateTimePicker,
    DaysOfWeekSelector,
    DurationPicker,
    FileInput,
    FileUpload,
    ImageComponent,
    Input,
    MapSelector,
    MultiCombobox,
    MultiImagesUpload,
    MultiSelect,
    OTP,
    Password,
    PhoneInput: PhoneInputField,
    RadioButton,
    RichTextField,
    Select,
    SingleImageUpload,
    Switch,
    TextArea,
    TimePicker
  },
  fieldContext,
  formComponents: {
    SubmitButton
  },
  formContext
})

function useAppFormWithLocaleRevalidation(
  ...args: Parameters<typeof useAppFormBase>
): ReturnType<typeof useAppFormBase> {
  const form = useAppFormBase(...args)
  useRevalidateFormOnLocaleChange(
    form as Parameters<typeof useRevalidateFormOnLocaleChange>[0]
  )
  return form
}

export const useAppForm = useAppFormWithLocaleRevalidation as typeof useAppFormBase

export { withFieldGroup, withForm }
